// src/components/ResetPasswordModal.tsx

import React, { useState } from "react";
import { FaTimes, FaEye, FaEyeSlash, FaKey } from "react-icons/fa";

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReset: (newPassword: string) => void;
  userName: string;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  onReset,
  userName,
}) => {
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newPassword || !confirmPassword) {
      setError("Both fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setError("");
    onReset(newPassword);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-gunmetal-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-platinum-200 relative overflow-hidden animate-slideUp">
        {/* Top Decoration */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gunmetal-600 to-gunmetal-900"></div>

        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div className="flex items-center gap-3">
              <div className="bg-gunmetal-50 p-3 rounded-xl text-gunmetal-600 shadow-sm">
                <FaKey size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gunmetal-900 tracking-tight">
                  Reset Password
                </h2>
                <p className="text-[10px] font-semibold text-slate-grey-500 uppercase tracking-widest">
                  For {userName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-grey-400 hover:text-gunmetal-900 transition-colors p-1"
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="newPassword"
                className="block text-xs font-bold text-slate-grey-500 uppercase tracking-wide mb-2"
              >
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-alabaster-grey-50 border border-platinum-200 rounded-xl focus:ring-2 focus:ring-gunmetal-500/20 focus:border-gunmetal-500 outline-none transition-all text-gunmetal-900 font-medium placeholder:text-slate-grey-300 pr-12"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-grey-400 hover:text-gunmetal-600 focus:outline-none transition-colors"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-xs font-bold text-slate-grey-500 uppercase tracking-wide mb-2"
              >
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-alabaster-grey-50 border border-platinum-200 rounded-xl focus:ring-2 focus:ring-gunmetal-500/20 focus:border-gunmetal-500 outline-none transition-all text-gunmetal-900 font-medium placeholder:text-slate-grey-300 pr-12"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-grey-400 hover:text-gunmetal-600 focus:outline-none transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-xs p-3 rounded-xl border border-red-100 flex items-center justify-center font-bold animate-shake">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-white border border-platinum-200 text-slate-grey-600 font-bold rounded-xl hover:bg-platinum-50 transition-colors transform active:scale-[0.98]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-gunmetal-900 text-white font-bold rounded-xl hover:bg-gunmetal-800 transition-all shadow-lg hover:shadow-gunmetal-500/20 transform active:scale-[0.98]"
              >
                Reset Password
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
